import type { ContentBlock } from "@/features/content/content-types";
import type { Guide, GuideMeta } from "@/features/guides/types";
import { GUIDE_META } from "@/features/guides/generated/meta";
import { locales, defaultLocale, type Locale } from "@/lib/i18n/config";
import { registerLocaleResolver } from "@/lib/i18n/locale-availability";

const englishMeta = GUIDE_META[defaultLocale] ?? {};

/** Every English guide's meta, localized where a translated file exists — the fallback
 * guidesForLocale used to apply when it read full Guide objects instead of just meta.ts. */
function metaForLocale(locale: Locale): Record<string, GuideMeta> {
  const localized = GUIDE_META[locale] ?? {};
  const merged: Record<string, GuideMeta> = {};
  for (const [id, fallback] of Object.entries(englishMeta)) {
    merged[id] = localized[id] ?? fallback;
  }
  return merged;
}

/**
 * Locales with a real translated source file for this slug — the hreflang cluster.
 *
 * metaForLocale() falls back to the English meta so a reader never hits a dead page, but the
 * sitemap and the prerender only advertise locales that actually have their own file (see
 * scripts/generate-static-meta.ts#readContentMetas). Head tags must use this stricter set, not
 * the fallback-inclusive one — so this reads GUIDE_META directly, unmerged.
 */
export function localesForGuide(slug: string): Locale[] {
  return locales.filter((locale) => Object.values(GUIDE_META[locale] ?? {}).some((meta) => meta.slug === slug));
}

/**
 * Body text (blocks[]) is loaded lazily, one file at a time — see loadGuideBlocks below — because
 * it is the bulk of a guide's weight (184-210 KB per locale) and only the detail page ever reads
 * it. Every other consumer of a Guide here (GuidesPageClient, RelatedGuidesCard, Home's featured
 * guides, globalSearch) only ever touches `.meta`, so an empty array is a safe placeholder for
 * them. The real content is in the cache by the time GuideDetailPage renders it — main.tsx
 * preloads the one guide the current URL needs before hydrateRoot, and entry-server.tsx preloads
 * every guide up front since one Node process prerenders every locale.
 */
function toGuide(meta: GuideMeta, locale: Locale): Guide {
  return { meta, blocks: BLOCKS_CACHE.get(cacheKey(locale, meta.id)) ?? [] };
}

// Links obey the same rule as the sitemap and hreflang: a guide with no translated file for this
// locale is linked at its English URL, not at a locale URL the prerender never wrote. Registered
// rather than imported by locale-availability.ts so this module's eager glob of every guide body
// stays out of the eager Layout chunk — see registerLocaleResolver.
registerLocaleResolver("guides", localesForGuide);

export function getAllGuides(locale: Locale = defaultLocale): Guide[] {
  return Object.values(metaForLocale(locale))
    .map((meta) => toGuide(meta, locale))
    .sort((a, b) => new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime());
}

export function getGuideBySlug(locale: Locale, slug: string): Guide | undefined {
  const meta = Object.values(metaForLocale(locale)).find((m) => m.slug === slug);
  return meta ? toGuide(meta, locale) : undefined;
}

export function getRelatedGuides(locale: Locale, guide: Guide): Guide[] {
  if (!guide.meta.relatedGuides?.length) return [];
  const all = metaForLocale(locale);
  return guide.meta.relatedGuides
    .map((id) => all[id])
    .filter((meta): meta is GuideMeta => Boolean(meta))
    .map((meta) => toGuide(meta, locale));
}

/** Item kind -> guide tags (lowercased) that identify guides relevant to that kind's hobby. */
const KIND_GUIDE_TAGS: Partial<Record<string, string[]>> = {
  bird: ["birdwatching"],
  insect: ["insects", "insect-catching", "catch-insects"],
  fish: ["fishing"],
  crop: ["gardening", "crops"],
  flower: ["gardening", "flowers"],
  forageable: ["gardening"],
  animal: ["animals", "wildlife"],
  dish: ["cooking", "recipes"],
  ingredient: ["cooking", "recipes"],
};

/** Surfaces 1-3 guides relevant to an item's kind (e.g. a bird -> birdwatching guides), for the
 * "Related Guides" showcase on item detail pages. Matched by tag rather than category since
 * several categories (exploration, beginner) span multiple hobbies. */
export function getRelatedGuidesForKind(locale: Locale, kind: string, limit = 3): Guide[] {
  const tags = KIND_GUIDE_TAGS[kind];
  if (!tags?.length) return [];
  return getAllGuides(locale)
    .filter((guide) => guide.meta.tags?.some((tag) => tags.includes(tag.toLowerCase())))
    .slice(0, limit);
}

// ---- Lazy body loading ----

/**
 * One dynamic-import loader per content file, keyed by its Vite-relative path — Vite code-splits
 * each into its own chunk instead of bundling every locale's prose into whichever entry point
 * happens to import this registry (Home.tsx and lib/globalSearch.ts both do, unconditionally, but
 * only for `.meta`, which comes from GUIDE_META above and never touches this glob). `eager: false`
 * is the default; spelled out because flipping it back to `true` is exactly the regression this
 * file exists to prevent.
 */
const contentLoaders = import.meta.glob<{ guide: Guide }>("./content/*/*.ts", { eager: false });

const BLOCKS_CACHE = new Map<string, ContentBlock[]>();
const cacheKey = (locale: Locale, id: string) => `${locale}:${id}`;

/**
 * Loads one guide's body and caches it, idempotent per (locale, id). Falls back to the English
 * file when this locale has no translated one, same fallback metaForLocale applies to meta. Must
 * resolve before the guide detail page's first render — main.tsx preloads the one guide the
 * current URL needs (via preloadGuideBySlug) before hydrateRoot; entry-server.tsx preloads all of
 * them (loadAllGuideBlocks) before its first renderToString call.
 */
export async function loadGuideBlocks(locale: Locale, id: string): Promise<void> {
  const key = cacheKey(locale, id);
  if (BLOCKS_CACHE.has(key)) return;
  const loader =
    contentLoaders[`./content/${locale}/${id}.ts`] ?? contentLoaders[`./content/${defaultLocale}/${id}.ts`];
  if (!loader) return;
  const mod = await loader();
  BLOCKS_CACHE.set(key, mod.guide.blocks);
}

/** Resolves the filename id for a slug (the join key loadGuideBlocks needs — meta.id and the
 * filename are the same by convention, checked in generate-content-meta.ts) and preloads it. What
 * main.tsx calls for a `/guides/:slug` route before hydrating. */
export async function preloadGuideBySlug(locale: Locale, slug: string): Promise<void> {
  const meta = Object.values(metaForLocale(locale)).find((m) => m.slug === slug);
  if (meta) await loadGuideBlocks(locale, meta.id);
}

/** SSR-only: preloads every guide's body, every locale — see entry-server.tsx's
 * preloadTranslations for why the whole set has to be in memory before renderToString runs
 * rather than being discovered lazily mid-loop. */
export async function loadAllGuideBlocks(): Promise<void> {
  await Promise.all(
    locales.flatMap((locale) => Object.keys(metaForLocale(locale)).map((id) => loadGuideBlocks(locale, id))),
  );
}
